import type { Piece, Shape, ShootDay } from './clientSetup'

/**
 * WHAT “SOMETHING ELSE” ACTUALLY IS. 16 Oct 2026.
 *
 * It used to be one option that asked how many, what shape and when — the
 * three questions a film is made of — and then called whatever came out
 * *Piece 1*. Tom, twice: *"the something else offers me nothing"*, and then
 * *"the options don't correspond to what we might put in here."*
 *
 * The reason is that three unlike things were being posted through one
 * door:
 *
 *   COUNTABLE PIECES  cutdowns, a trailer, graphics, soundbites. These do
 *                     behave like films. They only ever needed a name and,
 *                     in two cases, one extra question.
 *   AN EVENT          a live stream is a date and a time. It is not a
 *                     quantity, and forcing it to be one is why the screen
 *                     felt wrong.
 *   WORK ON SOMETHING ELSE   subtitles, re-versions. Reviewed and left out
 *                     — they attach to a deliverable rather than being
 *                     one, which is a change to the shape of the data and
 *                     not to this screen.
 *
 * So Something else is now its own short pick-list, **and more than one
 * can be ticked** — Tom: *"there might be a combination of these things,
 * so give me the option to add all."* Each one ticked gets its own screen,
 * the same way Films and Podcast do, so the rhythm of the flow does not
 * change halfway through.
 *
 * THE RULE THIS FILE INHERITS, from utils/clientSetup.ts: **a question is
 * only asked if something saves it.** Two questions were specified and
 * then dropped for failing that test, and they should stay dropped until
 * there is somewhere to put the answer:
 *
 *   • *Subtitled?* on soundbites — no column, and putting it in the piece's
 *     title puts it on the client's page.
 *   • *Where does the stream go out?* — same. The venue is covered, because
 *     a stream day is a shoot day and shoot days already ask Where.
 *
 * **NOTHING IS IMPORTED FROM clientSetup AT RUNTIME.** That file imports
 * this one, so a value import here would close the loop and the constants
 * below would be built before their dependencies existed. The types are
 * erased at compile time and are safe; the two small constants are
 * duplicated on purpose, with the originals named in a comment.
 */

/** Mirrors BLANK_DAY in clientSetup.ts — see the note above. */
const EMPTY_DAY: ShootDay = { date: '', end: '', title: '', location: '' }
/** Mirrors FORMAT_NAME in clientSetup.ts. */
const LANDSCAPE = '16:9 Landscape'
const VERTICAL = '9:16 Vertical'

/**
 * "1 set", not "1 sets". 17 Oct 2026.
 *
 * Every count in the flow was written `${n} ${plural}` and so read "1
 * films", "1 sets", "1 trailers" on the summary — the screen a producer
 * checks before committing a job. Small, but it is the last thing read
 * before Create it, and it made the summary look generated rather than
 * written.
 *
 * A regex is enough and will stay enough: every plural in the flow is a
 * regular one (films, episodes, sets, pieces, cutdowns, trailers,
 * graphics, soundbites, streams). If an irregular one is ever added, give
 * its spec an explicit singular rather than growing this function.
 *
 * It lives here rather than in clientSetup.ts because the import only
 * runs one way — see the note above — and both files need it.
 */
export function countOf(count: number, plural: string): string {
  return `${count} ${count === 1 ? plural.replace(/s$/, '') : plural}`
}

export type ExtraKey = 'cutdowns' | 'trailer' | 'graphics' | 'soundbites' | 'stream' | 'named'

export interface ExtraSpec {
  key: ExtraKey
  label: string
  /** The plural used on the How many control. */
  pieces: string
  /** Where the count starts. */
  count: number
  /** Landscape / vertical / both — only for moving pictures. */
  shaped: boolean
  /** A few words, only where the name does not say it. */
  hint?: string
  /** Which of the type-specific questions this one asks. */
  asks: {
    /** 15 / 30 / 60 / mixed. Names the pieces. */
    length?: boolean
    /** One pack, or separate pieces. */
    pack?: boolean
    /** Days, a venue, and whether there is a recording. */
    stream?: boolean
    /** Free text — the old behaviour, kept as the escape hatch. */
    name?: boolean
  }
}

export const EXTRAS: ExtraSpec[] = [
  {
    key: 'cutdowns',
    label: 'Social cutdowns',
    pieces: 'cutdowns',
    count: 3,
    shaped: true,
    asks: { length: true },
  },
  { key: 'trailer', label: 'Trailer or teaser', pieces: 'trailers', count: 1, shaped: true, asks: {} },
  {
    key: 'graphics',
    label: 'Titles and graphics',
    pieces: 'graphics',
    count: 1,
    shaped: false,
    hint: 'Title sequence, lower thirds, animated logo',
    asks: { pack: true },
  },
  { key: 'soundbites', label: 'Soundbites', pieces: 'soundbites', count: 6, shaped: true, asks: {} },
  {
    key: 'stream',
    label: 'Live stream',
    pieces: 'streams',
    count: 1,
    shaped: false,
    hint: 'The days go on their schedule',
    asks: { stream: true },
  },
  {
    key: 'named',
    label: 'Something else',
    pieces: 'pieces',
    count: 2,
    shaped: true,
    hint: 'Name it yourself',
    asks: { name: true },
  },
]

export const extraSpec = (key: ExtraKey): ExtraSpec =>
  EXTRAS.find((item) => item.key === key) ?? EXTRAS[0]

/** How long a cutdown runs. Mixed means don't put a length in the name. */
export const LENGTHS: { key: string; label: string }[] = [
  { key: '15', label: '15 seconds' },
  { key: '30', label: '30 seconds' },
  { key: '60', label: '60 seconds' },
  { key: 'mixed', label: 'Mixed' },
]

export interface ExtraDetail {
  count: number
  ongoing: boolean
  due: string
  shape: Shape
  /** Cutdowns: '15' | '30' | '60' | 'mixed'. */
  length: string
  /** Graphics: one pack rather than separate pieces. */
  pack: boolean
  /** Something else: what it is called. */
  named: string
  /** Live stream: the days it runs, which become shoot days. */
  days: ShootDay[]
  /** Live stream: is there a recording to hand over afterwards? */
  recording: boolean
}

/** Cutdowns are vertical far more often than not; everything else isn't. */
export function blankExtra(key: ExtraKey): ExtraDetail {
  const spec = extraSpec(key)
  return {
    count: spec.count,
    ongoing: false,
    due: '',
    shape: key === 'cutdowns' ? 'vertical' : 'landscape',
    length: 'mixed',
    pack: true,
    named: '',
    days: [{ ...EMPTY_DAY }],
    recording: true,
  }
}

export function extraDetailOf(
  answers: { extraDetails: Record<string, ExtraDetail> },
  key: ExtraKey,
): ExtraDetail {
  return answers.extraDetails[key] ?? blankExtra(key)
}

/** "Mon 14 Sep" — split rather than Date-parsed, so no timezone shift. */
function dayLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

/** What this extra is called on its own screen. */
export function extraTitle(key: ExtraKey, detail: ExtraDetail): string {
  if (key === 'named' && detail.named.trim()) return detail.named.trim()
  return extraSpec(key).label
}

/** The stem every piece of this extra is named from. */
function stem(key: ExtraKey, detail: ExtraDetail): string {
  switch (key) {
    case 'cutdowns':
      return detail.length && detail.length !== 'mixed' ? `${detail.length}s cutdown` : 'Cutdown'
    case 'trailer':
      return 'Trailer'
    case 'graphics':
      return detail.pack ? 'Title pack' : 'Graphic'
    case 'soundbites':
      return 'Soundbite'
    case 'named':
      return detail.named.trim() || 'Piece'
    default:
      return 'Piece'
  }
}

interface ExtraAnswers {
  extras: ExtraKey[]
  extraDetails: Record<string, ExtraDetail>
}

/**
 * The days a live stream runs, as shoot days.
 *
 * A stream day IS a shoot day — the crew turn up, it has a venue and it
 * belongs on the client's schedule — so it is written to `project_shoots`
 * like any other rather than inventing a second kind of day. Giving the
 * day a title where there isn't one keeps the calendar from drawing a bare
 * date with nothing on it.
 */
export function streamDays(answers: ExtraAnswers): ShootDay[] {
  if (!answers.extras.includes('stream')) return []
  const detail = answers.extraDetails.stream
  if (!detail) return []
  return detail.days
    .filter((day) => day.date)
    .map((day) => ({ ...day, title: day.title.trim() || 'Live stream' }))
}

/**
 * Every piece the chosen extras start the job with.
 *
 * **One recording per stream day**, named after the day — Tom's call, with
 * the sensible proviso that it can be edited on the project page later if
 * a four-day event turns out to want one film. A stream with no recording
 * to hand over makes no pieces at all: the days still land on the
 * schedule, which is the whole of what the client sees.
 */
export function piecesFromExtras(answers: ExtraAnswers): Piece[] {
  const out: Piece[] = []

  for (const spec of EXTRAS) {
    if (!answers.extras.includes(spec.key)) continue
    const detail = extraDetailOf(answers, spec.key)

    if (spec.key === 'stream') {
      if (!detail.recording) continue
      for (const day of streamDays(answers)) {
        out.push({
          name: `${day.title} · ${dayLabel(day.date)}`,
          kind: 'other',
          category: 'Video',
          format: LANDSCAPE,
          due: detail.due,
        })
      }
      continue
    }

    /* A title pack is one thing however many graphics are in it. */
    const total = spec.key === 'graphics' && detail.pack ? 1 : Math.max(1, detail.count)
    const base = stem(spec.key, detail)
    const shapes: Shape[] = !spec.shaped
      ? ['landscape']
      : detail.shape === 'both'
        ? ['landscape', 'vertical']
        : [detail.shape]

    for (const shape of shapes) {
      for (let index = 0; index < total; index += 1) {
        const name = total === 1 ? base : `${base} ${index + 1}`
        out.push({
          name: shapes.length > 1 && shape === 'vertical' ? `${name} · Vertical` : name,
          kind: 'other',
          category: 'Video',
          format: spec.shaped ? (shape === 'vertical' ? VERTICAL : LANDSCAPE) : '',
          due: detail.due,
        })
      }
    }
  }

  return out
}

/** The line under each extra on the summary screen. */
export function extraSummary(key: ExtraKey, detail: ExtraDetail): string {
  const spec = extraSpec(key)

  if (key === 'stream') {
    const days = detail.days.filter((day) => day.date).length
    return [
      days === 0 ? 'No dates yet' : countOf(days, 'days'),
      detail.recording ? 'recording to deliver' : 'no recording',
    ].join(' · ')
  }

  const pack = spec.key === 'graphics' && detail.pack
  const total = pack ? 1 : Math.max(1, detail.count)
  return [
    pack
      ? 'One pack'
      : detail.ongoing
        ? `Ongoing · ${total} to start`
        : countOf(total, spec.pieces),
    spec.asks.length && detail.length !== 'mixed' ? `${detail.length}s` : '',
    spec.shaped && detail.shape !== 'landscape' ? detail.shape : '',
  ]
    .filter(Boolean)
    .join(' · ')
}
